'use client';

import * as React from 'react';
import { BadgeCheck } from 'lucide-react';

import { cn } from '../lib/utils';
import { Badge } from '../shadcn/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../shadcn/tooltip';

interface VerifiedSellerBadgeProps {
  /**
   * Defaults to "KYC verified + 1+ completed sale". Override only with
   * human-readable text; never pass a raw status code.
   */
  tooltip?: string;
  /** Small variant for use in listing cards; default size for detail pages. */
  size?: 'default' | 'sm';
  className?: string;
}

/**
 * Verified-seller signal used on marketplace listing detail + card.
 * Renders nothing if you pass `verified={false}` — keep that branch at the
 * call site so this component stays presentational.
 *
 * The `Tooltip` primitive requires `TooltipProvider` (enforced by the
 * `react-providers/require-provider` ESLint rule). We wrap it here so each
 * call site doesn't need to remember.
 */
export function VerifiedSellerBadge({
  tooltip = 'KYC verified + 1+ completed sale',
  size = 'default',
  className,
}: VerifiedSellerBadgeProps) {
  const isSmall = size === 'sm';
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant="success"
            // Focusable so keyboard users can reveal the tooltip (Radix shows it
            // on focus), and the reason is folded into aria-label so it is not
            // hover-only for screen-reader / touch users.
            tabIndex={0}
            role="img"
            className={cn(
              'focus-visible:ring-ring gap-1 focus-visible:ring-2 focus-visible:outline-none',
              isSmall ? 'h-5 px-1.5 text-[10px]' : '',
              className,
            )}
            aria-label={`Verified seller — ${tooltip}`}
          >
            <BadgeCheck
              className={isSmall ? 'h-3 w-3' : 'h-3.5 w-3.5'}
              aria-hidden="true"
            />
            <span>Verified</span>
          </Badge>
        </TooltipTrigger>
        <TooltipContent>{tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
